"use client";

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Product } from "@latam-energy/types";
import { ProductCard } from "./Cards";

export function ProductCatalog({ products }: { products: Product[] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [visible, setVisible] = useState(9);
  const q = searchParams.get("q") ?? "";
  const category = searchParams.get("categoria") ?? "";
  const brand = searchParams.get("marca") ?? "";
  const application = searchParams.get("aplicacion") ?? "";
  const sort = searchParams.get("orden") ?? "destacados";

  const categories = unique(products.map((p) => p.category));
  const brands = unique(products.map((p) => p.brand));
  const applications = unique(products.flatMap((p) => p.applications));

  const filtered = useMemo(() => {
    const query = q.toLowerCase();
    return products
      .filter((product) => !query || `${product.name} ${product.shortDescription} ${product.category} ${product.brand}`.toLowerCase().includes(query))
      .filter((product) => !category || product.category === category)
      .filter((product) => !brand || product.brand === brand)
      .filter((product) => !application || product.applications.includes(application))
      .sort((a, b) => {
        if (sort === "az") return a.name.localeCompare(b.name);
        if (sort === "categoria") return a.category.localeCompare(b.category);
        return Number(b.featured) - Number(a.featured);
      });
  }, [products, q, category, brand, application, sort]);

  function setParam(key: string, value: string) {
    const queryParams = new URLSearchParams(searchParams.toString());
    if (value) queryParams.set(key, value);
    else queryParams.delete(key);
    navigate(queryParams.toString() ? `/productos?${queryParams.toString()}` : "/productos");
    setVisible(9);
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
      <aside className="card h-fit p-5">
        <h2 className="text-lg font-extrabold text-navy">Filtros</h2>
        <label className="mt-4 grid gap-2 text-sm font-bold text-steel">
          Buscar
          <input className="rounded-md border border-slate-300 p-3" value={q} onChange={(event) => setParam("q", event.target.value)} />
        </label>
        <Select label="Categoría" value={category} options={categories} onChange={(value) => setParam("categoria", value)} />
        <Select label="Marca" value={brand} options={brands} onChange={(value) => setParam("marca", value)} />
        <Select label="Aplicación" value={application} options={applications} onChange={(value) => setParam("aplicacion", value)} />
        <Select label="Orden" value={sort} options={["destacados", "az", "categoria"]} onChange={(value) => setParam("orden", value)} />
        <button className="btn btn-outline mt-5 w-full" type="button" onClick={() => navigate("/productos")}>Limpiar filtros</button>
      </aside>
      <section aria-labelledby="catalog-results">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 id="catalog-results" className="text-xl font-extrabold text-navy">{filtered.length} resultado{filtered.length === 1 ? "" : "s"}</h2>
        </div>
        {filtered.length ? (
          <>
            <div className="grid-auto">{filtered.slice(0, visible).map((product) => <ProductCard key={product.slug} product={product} />)}</div>
            {visible < filtered.length ? <button className="btn btn-outline mx-auto mt-8 flex" onClick={() => setVisible((count) => count + 6)}>Cargar más</button> : null}
          </>
        ) : (
          <div className="card p-8 text-center">
            <h3 className="text-xl font-extrabold text-navy">Sin resultados</h3>
            <p className="mt-2 text-slate-600">Ajusta la búsqueda o limpia los filtros para ver más equipos.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <label className="mt-4 grid gap-2 text-sm font-bold text-steel">
      {label}
      <select className="rounded-md border border-slate-300 p-3" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Todos</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function unique(values: string[]) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}
